"use client";

import { useMemo, useState } from "react";
import {
  aggregateMatchedInventory,
  matchImportRows,
  parseCollectionCsv,
  rowsFromCardnexusExport,
  type ImportPreview,
} from "@/lib/collection-import";
import { cardnexusFetch } from "@/lib/cardnexus/browser";
import { mergeInventory, replaceInventory } from "@/lib/local-store";

type Props = {
  cards: Parameters<typeof matchImportRows>[1];
  onApplied: () => void;
  /** Affiche l'onglet sync API CardNexus (compte allowlisté uniquement). */
  allowCardnexusApi?: boolean;
};

export default function CollectionImportPanel({
  cards,
  onApplied,
  allowCardnexusApi = false,
}: Props) {
  const [tab, setTab] = useState<"csv" | "api">(allowCardnexusApi ? "api" : "csv");
  const [csvText, setCsvText] = useState("");
  const [mode, setMode] = useState<"set" | "add" | "replace">("replace");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [apiNote, setApiNote] = useState<string | null>(null);

  const stats = useMemo(() => {
    if (!preview) return null;
    return {
      matched: preview.matched.length,
      unmatched: preview.unmatched.length,
      qty: preview.totalQuantity,
    };
  }, [preview]);

  function runPreviewFromRows(rows: ReturnType<typeof parseCollectionCsv>) {
    const next = matchImportRows(rows, cards);
    setPreview(next);
    setError(
      next.matched.length === 0
        ? "Aucune carte reconnue. Vérifie le format (name/quantity ou set+number)."
        : null,
    );
  }

  function onCsvFile(file: File | null) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result ?? "");
      setCsvText(text);
      runPreviewFromRows(parseCollectionCsv(text));
    };
    reader.readAsText(file);
  }

  function previewCsv() {
    setError(null);
    runPreviewFromRows(parseCollectionCsv(csvText));
  }

  /** Sync live via GET /inventory (recommandé) — pas d'export bulk rate-limité. */
  async function syncCardnexusLive() {
    setBusy(true);
    setError(null);
    setPreview(null);
    setApiNote(null);
    try {
      const data = await cardnexusFetch<{
        lines: Array<{
          quantity: number;
          name?: string | null;
          printNumber?: string | null;
          expansionCode?: string | null;
          cardId?: string | null;
        }>;
        mirror: Array<{ cardId: string; quantity: number }>;
        matched?: number;
        unmatched?: number;
        count?: number;
      }>("/api/cardnexus/inventory");

      // Prefer already-matched mirror from the API.
      if (data.mirror?.length) {
        const fakeRows = data.mirror.map((m) => {
          const card = cards.find((c) => c.id === m.cardId);
          return {
            name: card?.name,
            setCode: card?.setId ?? undefined,
            number: card?.collectorNumber != null ? String(card.collectorNumber) : undefined,
            quantity: m.quantity,
          };
        });
        runPreviewFromRows(fakeRows);
        setApiNote(
          `Inventaire live · ${data.matched ?? data.mirror.length} cartes` +
            (data.unmatched ? ` · ${data.unmatched} non reconnue(s)` : ""),
        );
        return;
      }

      const rows = rowsFromCardnexusExport(
        (data.lines ?? []).map((l) => ({
          game: "riftbound",
          quantity: l.quantity,
          name: l.name,
          printNumber: l.printNumber,
          setCode: l.expansionCode,
          expansion: l.expansionCode,
        })),
      );
      runPreviewFromRows(rows);
      setApiNote(
        data.count
          ? `${data.count} ligne(s) récupérée(s) — matching local`
          : "Aucune ligne Riftbound chez CardNexus",
      );
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Sync CardNexus impossible");
    } finally {
      setBusy(false);
    }
  }

  /** Fallback bulk export (1 / 10 min) si besoin d'un dump brut. */
  async function syncCardnexusBulk() {
    setBusy(true);
    setError(null);
    setPreview(null);
    setApiNote(null);
    try {
      const res = await fetch("/api/cardnexus/sync", {
        method: "POST",
        credentials: "same-origin",
      });
      const data = (await res.json()) as { error?: string; items?: unknown[] };
      if (!res.ok) throw new Error(data.error || "Sync échouée");
      const rows = rowsFromCardnexusExport(data.items ?? []);
      runPreviewFromRows(rows);
      setApiNote(`Export bulk · ${rows.length} ligne(s)`);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Sync CardNexus impossible");
    } finally {
      setBusy(false);
    }
  }

  function applyImport() {
    if (!preview?.matched.length) return;
    const entries = aggregateMatchedInventory(preview.matched);
    if (mode === "replace") {
      replaceInventory(entries);
    } else {
      mergeInventory(entries, mode === "add" ? "add" : "set");
    }
    onApplied();
    setPreview(null);
    setCsvText("");
  }

  return (
    <div className="glass rounded-2xl border border-white/10 p-4 sm:p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold">Importer la collection</h2>
          <p className="mt-1 text-sm text-gray-400">
            {allowCardnexusApi
              ? "CSV manuel, ou sync live de ton inventaire CardNexus."
              : "CSV (export CardNexus / manuel)."}
          </p>
        </div>
        {allowCardnexusApi ? (
          <div className="flex rounded-xl border border-white/10 bg-black/30 p-1 text-sm">
            <button
              type="button"
              onClick={() => setTab("csv")}
              className={`rounded-lg px-3 py-1.5 font-medium ${
                tab === "csv" ? "bg-cyan-600 text-white" : "text-gray-400 hover:text-white"
              }`}
            >
              CSV
            </button>
            <button
              type="button"
              onClick={() => setTab("api")}
              className={`rounded-lg px-3 py-1.5 font-medium ${
                tab === "api" ? "bg-cyan-600 text-white" : "text-gray-400 hover:text-white"
              }`}
            >
              API CardNexus
            </button>
          </div>
        ) : null}
      </div>

      {tab === "csv" || !allowCardnexusApi ? (
        <div className="space-y-3">
          <input
            type="file"
            accept=".csv,text/csv,text/plain"
            onChange={(e) => onCsvFile(e.target.files?.[0] ?? null)}
            className="block w-full text-sm text-gray-400 file:mr-3 file:rounded-lg file:border-0 file:bg-white/10 file:px-3 file:py-1.5 file:text-sm file:text-white hover:file:bg-white/15"
          />
          <textarea
            value={csvText}
            onChange={(e) => setCsvText(e.target.value)}
            placeholder={`name,quantity,set,number\nDarius,3,OGN,12\n…\nou colonnes CardNexus: name,quantity,printNumber,expansion`}
            rows={6}
            className="w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2 font-mono text-xs outline-none focus:border-cyan-500/50"
          />
          <button
            type="button"
            onClick={previewCsv}
            className="rounded-xl bg-white/10 px-4 py-2 text-sm font-semibold hover:bg-white/15"
          >
            Prévisualiser
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-gray-500">
            Utilise le compte CardNexus déjà lié (OAuth). Le sync live lit{" "}
            <code className="text-cyan-300/80">GET /inventory</code> — recommandé. L&apos;export
            bulk est limité à ~1 / 10 min.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => void syncCardnexusLive()}
              className="rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 px-4 py-2 text-sm font-bold disabled:opacity-40"
            >
              {busy ? "Sync en cours…" : "Synchroniser (live)"}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void syncCardnexusBulk()}
              className="rounded-xl border border-white/15 px-4 py-2 text-sm font-medium text-gray-300 hover:bg-white/5 disabled:opacity-40"
            >
              Export bulk
            </button>
          </div>
          {apiNote && <p className="text-xs text-cyan-200/80">{apiNote}</p>}
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <label className="text-sm text-gray-400">Mode d&apos;import</label>
        <select
          value={mode}
          onChange={(e) => setMode(e.target.value as typeof mode)}
          className="rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-sm"
        >
          <option value="set">Mettre à jour les quantités (cartes du fichier)</option>
          <option value="add">Ajouter aux quantités existantes</option>
          <option value="replace">Remplacer tout l&apos;inventaire</option>
        </select>
      </div>

      {error && (
        <p className="mt-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-100">
          {error}
        </p>
      )}

      {stats && (
        <div className="mt-4 space-y-3">
          <p className="text-sm text-gray-300">
            {stats.matched} cartes reconnues · {stats.qty} copies · {stats.unmatched} non
            reconnues
          </p>
          {preview && preview.unmatched.length > 0 && (
            <details className="rounded-xl border border-white/10 bg-black/20 p-3 text-xs text-gray-400">
              <summary className="cursor-pointer font-medium text-gray-300">
                Voir les non reconnues ({preview.unmatched.length})
              </summary>
              <ul className="mt-2 max-h-40 space-y-1 overflow-y-auto">
                {preview.unmatched.slice(0, 40).map((u, i) => (
                  <li key={i}>
                    {u.row.name || u.row.printNumber || u.row.setCode || "?"} ×{u.quantity}
                  </li>
                ))}
              </ul>
            </details>
          )}
          <button
            type="button"
            disabled={!preview?.matched.length}
            onClick={applyImport}
            className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-bold hover:bg-emerald-500 disabled:opacity-40"
          >
            Appliquer à l&apos;inventaire
          </button>
        </div>
      )}
    </div>
  );
}
